import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from '@/hooks/useInView';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { DURATION } from '@/lib/motion';

interface CountUpProps {
  /** Final value to count to. */
  value: number;
  /** Rendered after the number, e.g. "+" or "%". */
  suffix?: string;
  /** Animation duration in ms. */
  duration?: number;
  className?: string;
}

/**
 * Animated stat counter: counts from 0 to `value` the first time it scrolls
 * into view. Uses requestAnimationFrame with an ease-out curve — no timers,
 * no library. With prefers-reduced-motion (or no IntersectionObserver) the
 * final value renders immediately, so the number is never wrong or hidden.
 */
const CountUp = ({ value, suffix = '', duration = DURATION.slow, className }: CountUpProps) => {
  const reduced = useReducedMotion();
  const [ref, inView] = useInView<HTMLSpanElement>({ threshold: 0.4 });
  // Default to the real value so no-JS, crawler, and snapshot readers never see
  // "0". The count-up is a progressive enhancement: before first paint we reset
  // to 0 only when motion is allowed and IntersectionObserver can bring us back.
  const [display, setDisplay] = useState(value);
  const started = useRef(false);

  useLayoutEffect(() => {
    if (reduced || typeof IntersectionObserver === 'undefined') return;
    setDisplay(0);
    // Safety net: if the element never intersects (headless captures, hidden
    // tabs), still show the real value instead of stranding the counter at 0.
    const safety = window.setTimeout(() => {
      if (!started.current) setDisplay(value);
    }, 2500);
    return () => window.clearTimeout(safety);
  }, [reduced, value]);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    if (!inView || started.current) return;
    started.current = true;

    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduced, value, duration]);

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">
        {value}
        {suffix}
      </span>
      <span aria-hidden="true">
        {display}
        {suffix}
      </span>
    </span>
  );
};

export default CountUp;
